/**
 * Purpose:
 *   Renders the mobile sidebar drawer for the navigation menu.
 *
 * Notes:
 *   Receives localized labels from Menu via the common namespace.
 */

import { cx } from '@/util/functions/cx';
import { MenuItems } from './MenuItems';
import {
  MENU_CONTAINER_MOBILE,
  MENU_CONTAINER_MOBILE_OPEN,
  MENU_OVERLAY,
  MENU_OVERLAY_OPEN,
  MENU_DRAWER,
  MENU_DRAWER_OPEN,
  MENU_DRAWER_HEADER_SPACER,
} from './Menu.styles';
import type { MenuItemConfig } from '@/types/menu';

interface IMenuMobileDrawerProps {
  isOpen: boolean;
  items: MenuItemConfig[];
  expandedItems: Set<string>;
  isActive: (item: MenuItemConfig) => boolean;
  isParentActive: (item: MenuItemConfig) => boolean;
  onToggle: (itemId: string) => void;
  onNavigate: (href: string) => void;
  onClose?: () => void;
  navigationLabel: string;
  closeLabel: string;
}

export function MenuMobileDrawer({
  isOpen,
  items,
  expandedItems,
  isActive,
  isParentActive,
  onToggle,
  onNavigate,
  onClose,
  navigationLabel,
  closeLabel,
}: IMenuMobileDrawerProps) {
  if (!isOpen) return null;

  return (
    <div className={cx(MENU_CONTAINER_MOBILE, MENU_CONTAINER_MOBILE_OPEN)}>
      <button
        type="button"
        className={cx(MENU_OVERLAY, MENU_OVERLAY_OPEN)}
        onClick={onClose}
        aria-label={closeLabel}
      />

      <aside
        className={cx(MENU_DRAWER, MENU_DRAWER_OPEN)}
        aria-label={navigationLabel}
        role="dialog"
        aria-modal="true"
      >
        <div className={MENU_DRAWER_HEADER_SPACER} aria-hidden="true" />
        <MenuItems
          items={items}
          ariaLabel={navigationLabel}
          expandedItems={expandedItems}
          isActive={isActive}
          isParentActive={isParentActive}
          onToggle={onToggle}
          onNavigate={onNavigate}
          isCollapsed={false}
          onLinkClick={onClose}
        />
      </aside>
    </div>
  );
}
